/**
 * ImageConfigPanel — catalog-native image configuration dropdowns.
 *
 * Reads ImageCapability from CatalogModel directly (discriminated union on
 * capability.modality === 'image'). Renders sizing and quality controls based
 * on the capability schema; emits atomic patches via onChange.
 *
 * Sizing modes:
 *  - discrete_size    → Size dropdown from sizing.sizes
 *  - aspect_ratio     → Aspect Ratio dropdown from sizing.aspect_ratios
 *  - constrained_size → Size dropdown from sizing.sizes
 *
 * Quality dropdown only renders when capability.quality_options is non-empty.
 * No web_search toggle — supports_web_search is dead data (always false).
 */

import { useId } from 'react'
import { cn } from '@/lib/cn'
import { useI18n } from '@/i18n'
import type { CatalogModel, ImageSizing } from '@/types'

function assertNever(x: never): never {
  throw new Error(`Unhandled ImageSizing mode: ${JSON.stringify(x)}`)
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ImageConfigPanelValue {
  size?: string | null
  aspect_ratio?: string | null
  quality?: string | null
}

export interface ImageConfigPanelProps {
  model: CatalogModel
  value: ImageConfigPanelValue
  onChange: (patch: { size?: string; aspect_ratio?: string; quality?: string }) => void
  disabled?: boolean
  className?: string
}

// ---------------------------------------------------------------------------
// Shared select styles (DS tokens only)
// ---------------------------------------------------------------------------

const selectCls = (disabled?: boolean) =>
  cn(
    'w-full px-3 py-2 border border-input rounded-lg text-sm transition-colors',
    'bg-card text-foreground',
    'focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-ring focus:ring-ring',
    disabled && 'bg-muted text-muted-foreground cursor-not-allowed',
  )

const labelCls = 'block text-sm font-medium text-foreground mb-1'

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ImageConfigPanel({
  model,
  value,
  onChange,
  disabled = false,
  className,
}: ImageConfigPanelProps) {
  const uid = useId()
  const { t } = useI18n()

  if (model.capability.modality !== 'image') return null

  const cap = model.capability
  const { sizing, quality_options } = cap

  const sizeId = `${uid}-size`
  const aspectId = `${uid}-aspect`
  const qualityId = `${uid}-quality`

  const defaultLabel = t('playground.labels.defaultOption')
  const sizingPlaceholder = cap.default_size ? `${defaultLabel} (${cap.default_size})` : defaultLabel

  const sizingControl = (() => {
    const s: ImageSizing = sizing
    if (s.mode === 'discrete_size') {
      return (
        <div className="w-full">
          <label htmlFor={sizeId} className={labelCls}>
            {t('playground.labels.resolution')}
          </label>
          <select
            id={sizeId}
            value={value.size ?? ''}
            onChange={(e) => onChange({ size: e.target.value })}
            disabled={disabled}
            className={selectCls(disabled)}
          >
            <option value="">{sizingPlaceholder}</option>
            {s.sizes.map((sz) => (
              <option key={sz} value={sz}>
                {sz}
              </option>
            ))}
          </select>
        </div>
      )
    }
    if (s.mode === 'constrained_size') {
      return (
        <div className="w-full">
          <label htmlFor={sizeId} className={labelCls}>
            {t('playground.labels.resolution')}
          </label>
          <select
            id={sizeId}
            value={value.size ?? ''}
            onChange={(e) => onChange({ size: e.target.value })}
            disabled={disabled}
            className={selectCls(disabled)}
          >
            <option value="">{sizingPlaceholder}</option>
            {s.sizes.map((sz) => (
              <option key={sz} value={sz}>
                {sz}
              </option>
            ))}
          </select>
        </div>
      )
    }
    if (s.mode === 'aspect_ratio') {
      return (
        <div className="w-full">
          <label htmlFor={aspectId} className={labelCls}>
            {t('playground.labels.aspectRatio')}
          </label>
          <select
            id={aspectId}
            value={value.aspect_ratio ?? ''}
            onChange={(e) => onChange({ aspect_ratio: e.target.value })}
            disabled={disabled}
            className={selectCls(disabled)}
          >
            <option value="">{defaultLabel}</option>
            {s.aspect_ratios.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
      )
    }
    return assertNever(s)
  })()

  return (
    <div className={cn('space-y-3', className)}>
      {/* Sizing — mode-dispatched with exhaustiveness guard */}
      {sizingControl}

      {/* Quality — only if model exposes quality options */}
      {quality_options.length > 0 && (
        <div className="w-full">
          <label htmlFor={qualityId} className={labelCls}>
            {t('playground.labels.quality')}
          </label>
          <select
            id={qualityId}
            value={value.quality ?? ''}
            onChange={(e) => onChange({ quality: e.target.value })}
            disabled={disabled}
            className={selectCls(disabled)}
          >
            <option value="">{defaultLabel}</option>
            {quality_options.map((q) => (
              <option key={q} value={q}>
                {q}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  )
}
